import {
    CheckCircleIcon,
    PencilIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';

import { Checkbox } from '@/components/core/Checkbox';
import { Icon } from '@/components/core/Icon';
import { IconButton } from '@/components/core/IconButton';
import { StatusColumn } from '@/components/shared';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { toPixelColumnSize } from '@/services/utils';
import { DataModel, DataModelAttribute } from '@/types/DataModel.ts';

import { BulkSubscription } from './bulk-subscriptions.types';

interface ColumnsProps {
    t: (key: string) => string;
    onEdit: (entity: BulkSubscription) => void;
    onDelete: (entityOrEntities: BulkSubscription | BulkSubscription[]) => void;
    onUpdateEntries: (entity: BulkSubscription) => void;
    dataModel: DataModel | undefined;
    translateAttributeLabel: (attribute: DataModelAttribute) => string;
    isViewer?: boolean;
}

export const columns: (props: ColumnsProps) => ColumnDef<BulkSubscription>[]
    = ({ t, onDelete, onUpdateEntries, dataModel, translateAttributeLabel, isViewer = false }) => ([
        {
            id: 'select',
            size: 40,
            enableResizing: false,
            meta: {
                cellClassName: 'tw-p-0 tw-h-full tw-text-center',
            },
            header: ({ table }) => {
                const allSelected = table.getIsAllRowsSelected();
                const someSelected = table.getIsSomeRowsSelected();
                return (
                    <div className="tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10">
                        <Checkbox
                            isChecked={allSelected}
                            isIndeterminate={!allSelected && someSelected}
                            onCheckedChange={(value) => {
                                table.getToggleAllRowsSelectedHandler()({ target: { value } });
                            }}
                        />
                    </div>
                );
            },
            cell: ({ row, table }) => (
                <div className="tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10">
                    <Checkbox
                        isChecked={row.getIsSelected()}
                        disabled={table.getIsAllRowsSelected()}
                        onCheckedChange={row.getToggleSelectedHandler()}
                    />
                </div>
            ),
        },
        {
            accessorKey: 'name',
            header: t('BULK_SUBSCRIPTION.NAME'),
            size: toPixelColumnSize(0.10),
            minSize: 90,
        },
        {
            accessorKey: 'label',
            header: t('CUSTOMIZATION.LABEL'),
            size: toPixelColumnSize(0.08),
            minSize: 80,
        },
        {
            accessorKey: 'status',
            header: t('BULK_SUBSCRIPTION.STATUS'),
            size: toPixelColumnSize(0.06),
            minSize: 60,
            cell: ({ row }) => <StatusColumn status={row.getValue('status')} />
        },
        {
            accessorKey: 'active',
            header: t('ACTIVE'),
            size: toPixelColumnSize(0.04),
            minSize: 50,
            cell: ({ row }) =>
                !row.getValue('active') === false && <Icon icon={CheckCircleIcon} size={'lg'} />
        },
        {
            accessorKey: 'id',
            header: t('BULK_SUBSCRIPTION.ID'),
            size: toPixelColumnSize(0.04),
            minSize: 50,
        },
        {
            accessorKey: 'attributes',
            header: t('ATTRIBUTES'),
            size: toPixelColumnSize(0.11),
            minSize: 120,
            cell: ({ row }) => {
                let attrs = (row.getValue('attributes') as string[]);
                attrs = attrs.map((attributeName: string) => {
                    const attribute = dataModel?.attribute(attributeName);
                    return attribute ? translateAttributeLabel(attribute) : attributeName;
                }
                );
                const limit = 4;
                if (attrs.length > limit) {
                    attrs = attrs.slice(0, limit);
                    attrs.push('...');
                }
                return (<div className="tw-whitespace-nowrap">
                    {attrs.join(', ')}
                </div>);
            },
        },
        {
            accessorKey: 'description',
            header: t('DATA_MODEL.DESCRIPTION'),
            size: toPixelColumnSize(0.11),
            minSize: 100,
        },
        {
            accessorKey: 'createdAt',
            header: t('CREATED_AT'),
            size: toPixelColumnSize(0.07),
            minSize: 90,
            cell: ({ row }) => (
                <div className="tw-whitespace-nowrap">
                    {new Date(row.getValue('createdAt')).toLocaleString()}
                </div>
            ),
        },
        {
            accessorKey: 'createdByName',
            header: t('CREATED_BY'),
            size: toPixelColumnSize(0.06),
            minSize: 90,
            cell: ({ row }) => {
                // FIXME: Use a hook to load and cache the user names
                const angularJsInjector = getInjector();
                const $rootScope = getRootScope(angularJsInjector);
                return (
                    <div className="tw-whitespace-nowrap">
                        {$rootScope.getUserName(row.getValue('createdByName'))}
                    </div>
                );
            },
        },
        {
            accessorKey: 'updatedAt',
            header: t('UPDATED_AT'),
            size: toPixelColumnSize(0.07),
            minSize: 90,
            cell: ({ row }) => (
                <div className="tw-whitespace-nowrap">
                    {row.getValue('updatedAt') ? new Date(row.getValue('updatedAt')).toLocaleString() : ''}
                </div>
            ),
        },
        {
            accessorKey: 'updatedByName',
            header: t('UPDATED_BY'),
            size: toPixelColumnSize(0.06),
            minSize: 90,
            cell: ({ row }) => {
                // FIXME: Use a hook to load and cache the user names
                const angularJsInjector = getInjector();
                const $rootScope = getRootScope(angularJsInjector);
                return (
                    <div className="tw-whitespace-nowrap">
                        {$rootScope.getUserName(row.getValue('updatedByName'))}
                    </div>
                );
            },
        },
        ...(!isViewer ? [{
            id: 'actions',
            header: t('Actions'),
            size: 90,
            enableResizing: false,
            meta: {
                pinned: true,
            },
            cell: ({ row }: any) =>
                <div className="tw-flex tw-flex-row-reverse tw-items-center">
                    <IconButton
                        title={t('ACTION.DELETE')}
                        icon={TrashIcon}
                        variant="destructive"
                        className="tw-icon-primary"
                        onClick={() => onDelete(row.original)}
                    />
                    <IconButton
                        title={t('ACTION.EDIT')}
                        icon={PencilIcon}
                        variant="default"
                        className="tw-icon-primary"
                        onClick={() => onUpdateEntries(row.original)}
                    />
                </div>
        }] : [])
    ]);
